import {FormArray, FormControl, FormGroup, Validators} from '@angular/forms';

import {map} from 'rxjs/operators';
import {of} from 'rxjs/internal/observable/of';
import {forkJoin, Observable, Subscription} from 'rxjs';
import {switchMap} from 'rxjs/internal/operators/switchMap';

import {
    ApiError,
    ApiResponse,
    HttpStatusCodesEnum,
    DocumentTypesEnum,
    IdentityDocumentTypesEnum,
    CompanyDocumentsService,
    IndividualDocumentsService,
    CompanyDocumentInterface,
    IndividualDocumentInterface,
    LookupModel,
    CompanyDocumentModel,
    IndividualDocumentModel,
    CompanyDocumentStatusTriggerModel,
    IndividualDocumentStatusTriggerModel,
} from '@spa/api';

import {InArrayValidator} from '@spa/validators';

import {SelectOptionInterface} from '../../../../shared/components';
import {BaseControl, Helper, SelectControl} from '../../../../shared/classes';

import {UploadedDocumentInitDataInterface} from '../../interfaces';

import {
    UploadedDocumentControls,
    UploadedDocumentIdSelector,
    UploadedDocumentTypeSelector,
    UploadedDocumentStatusSelector,
    UploadedDocumentSubTypeSelector,
    UploadedDocumentsFormArraySelector,
} from '../../../../../assets/descriptors';

export class UploadedDocumentsForm {
    private availableDocumentsStatusTriggers: UploadedDocumentInitDataInterface[];

    public readonly uploadedDocumentsFormArraySelector: string;

    public form: FormGroup;
    public formGroupSubscriptions: Subscription[];
    public controls: Array<BaseControl<number | string>[]>;

    constructor(
        private readonly documentTypes: LookupModel,
        private readonly identityDocumentTypes: LookupModel,
        private readonly proofOfAddressDocumentTypes: LookupModel,
        private readonly individualDocumentsService: IndividualDocumentsService,
        private readonly companyDocumentsService: CompanyDocumentsService,
        private readonly isCorporateClient: boolean,
        private documents: IndividualDocumentModel[] | CompanyDocumentModel[],
    ) {
        this.availableDocumentsStatusTriggers = [];
        this.formGroupSubscriptions = [];
        this.uploadedDocumentsFormArraySelector = UploadedDocumentsFormArraySelector;
    }

    private createRequestsForAvailableStatuses(): Observable<ApiResponse<ApiError[] | UploadedDocumentInitDataInterface>>[] {
        if (!this.isCorporateClient) {
            return this.getIndividualDocumentsAvailableStatuses();
        }

        return (this.documents as CompanyDocumentModel[]).map((document: CompanyDocumentModel) => {
            const filteredAvailableStatuses = this.availableDocumentsStatusTriggers
                .filter((statusesObj: UploadedDocumentInitDataInterface) => document.id === statusesObj.id);

            if (filteredAvailableStatuses.length !== 0) {
                return Helper.createSuccessResponseObservable<UploadedDocumentInitDataInterface>(
                    filteredAvailableStatuses[0]
                );
            }

            return this.companyDocumentsService.getStatusTriggers(document.id).pipe(
                map(this.getStatusTriggersResponseMap.bind(this, document))
            );
        });
    }

    private getIndividualDocumentsAvailableStatuses(): Observable<ApiResponse<ApiError[] | UploadedDocumentInitDataInterface>>[] {
        return (this.documents as IndividualDocumentModel[]).map((document: IndividualDocumentModel) => {
            const filteredAvailableStatuses = this.availableDocumentsStatusTriggers
                .filter((statusesObj: UploadedDocumentInitDataInterface) => document.id === statusesObj.id);

            if (filteredAvailableStatuses.length !== 0) {
                return Helper.createSuccessResponseObservable<UploadedDocumentInitDataInterface>(filteredAvailableStatuses[0]);
            }

            return this.individualDocumentsService.getStatusTriggers(document.id).pipe(
                map(this.getStatusTriggersResponseMap.bind(this, document))
            );
        });
    }

    private getStatusTriggersResponseMap(
        document: IndividualDocumentModel | CompanyDocumentModel,
        response: ApiResponse<ApiError[] | number[]>
    ): ApiResponse<ApiError[] | UploadedDocumentInitDataInterface> {
        if (response.status !== HttpStatusCodesEnum.Ok) {
            return Helper.errorsHelper().createErrorResponse<number[]>(response);
        }

        const availableDocumentStatuses = {
            id: document.id,
            type: document.documentType,
            subType: document.subDocumentType,
            status: document.status,
            availableTriggers: response.data as number[]
        };

        this.availableDocumentsStatusTriggers.push(availableDocumentStatuses);

        return Helper.createSuccessResponse<UploadedDocumentInitDataInterface>(availableDocumentStatuses);
    }

    private initDocumentFormGroup(data: UploadedDocumentInitDataInterface): FormGroup {
        const formGroup = new FormGroup({});

        const controls = UploadedDocumentControls().map((control: BaseControl) => {
            switch (control.name) {
                case UploadedDocumentIdSelector:
                    control.value = data.id;
                    break;
                case UploadedDocumentTypeSelector:
                    control.value = data.type;

                    (control as SelectControl).options = this.createDocumentTypeControlOptions();

                    control.extendValidators(InArrayValidator(this.documentTypes.getTypeKeys()));
                    break;
                case UploadedDocumentSubTypeSelector:
                    control.value = data.subType;
                    control = this.modifyDocumentSubTypeControl(data.type, control as SelectControl);
                    break;
                case UploadedDocumentStatusSelector:
                    control.value = data.status;

                    const controlConfig = this.getConfigForStatusControl(data.availableTriggers);

                    (control as SelectControl).options = controlConfig.options.concat({
                        label: Helper.textsHelper().getDocumentStatusName(data.status),
                        value: data.status
                    }).sort((a: SelectOptionInterface, b: SelectOptionInterface) => {
                        return (a.value as number) - (b.value as number);
                    });

                    control.extendValidators(InArrayValidator(controlConfig.statusIds.concat([data.status])));

                    break;
            }

            formGroup.addControl(control.name, new FormControl(control.value, control.validators));

            return control;
        }) as BaseControl<number>[];

        this.controls.push(controls);

        this.formGroupSubscriptions.push(formGroup.valueChanges.subscribe(this.formValueChangeHandler.bind(this)));

        return formGroup;
    }

    private createDocumentTypeControlOptions(): SelectOptionInterface[] {
        return this.documentTypes.getTypeKeys()
            .filter((key: string) => {
                return ([
                    DocumentTypesEnum.Selfie,
                    DocumentTypesEnum.ProofOfAddress,
                    DocumentTypesEnum.ProofOfIdentity,
                    DocumentTypesEnum.CertificateOfIncorporation,
                    DocumentTypesEnum.CertificateOfCurrentStanding,
                    DocumentTypesEnum.ArticleOfAssociation,
                    DocumentTypesEnum.MemorandumOfAssociation,
                    DocumentTypesEnum.RegisterOfDirectors,
                    DocumentTypesEnum.RegisterOfShareholders
                ] as string[]).indexOf(key) >= 0;
            })
            .map((key: string) => {
                return {
                    label: this.documentTypes.getTypeValue(key),
                    value: key
                };
            });
    }

    private modifyDocumentSubTypeControl(
        documentType: string,
        control: SelectControl
    ): SelectControl {
        let types = null;

        switch (documentType) {
            case DocumentTypesEnum.ProofOfAddress:
                types = this.proofOfAddressDocumentTypes;
                break;
            case DocumentTypesEnum.ProofOfIdentity:
                types = this.identityDocumentTypes;
                break;
            default:
                control.options = [];
        }

        control.resetValidators();

        if (types) {
            let typeKeys = types.getTypeKeys();

            if (documentType === DocumentTypesEnum.ProofOfIdentity) {
                typeKeys = typeKeys.filter((key: string) => key !== IdentityDocumentTypesEnum.Unspecified);
            }

            control.options = typeKeys.map((key: string) => {
                return {
                    label: types.getTypeValue(key),
                    value: key
                };
            });

            control.setValidators([Validators.required, InArrayValidator(typeKeys)]);
        }

        return control;
    }

    private getConfigForStatusControl(triggers: number[]): { options: SelectOptionInterface[], statusIds: number[] } {
        return triggers.reduce((reducedObject: { options: SelectOptionInterface[], statusIds: number[] }, trigger: number) => {
            const status = Helper.enumsHelper().getDocumentStatusByTrigger(trigger);

            reducedObject.options.push({
                label: Helper.textsHelper().getDocumentStatusNameByTrigger(trigger),
                value: status
            });

            reducedObject.statusIds.push(status);

            return reducedObject;
        }, {options: [], statusIds: []}) as { options: SelectOptionInterface[], statusIds: number[] };
    }

    private formValueChangeHandler(data: { [key: string]: number | string }): void {
        const controlGroups = this.controls.filter((controls: BaseControl[]) => {
            const idControls = controls
                .filter((control: BaseControl) => control.name === UploadedDocumentIdSelector);

            return (idControls.length !== 0) && (idControls[0].value === data[UploadedDocumentIdSelector]);
        });

        if (controlGroups.length !== 0) {
            const typeControls = controlGroups[0]
                .filter((control: BaseControl) => control.name === UploadedDocumentTypeSelector);

            if ((typeControls.length !== 0) && (typeControls[0].value !== data[UploadedDocumentTypeSelector])) {
                const subTypeControls = controlGroups[0]
                    .filter((control: BaseControl) => control.name === UploadedDocumentSubTypeSelector);

                if (subTypeControls.length !== 0) {
                    const formArray = this.form.controls[this.uploadedDocumentsFormArraySelector] as FormArray;

                    subTypeControls[0].value = null;

                    subTypeControls[0] = this.modifyDocumentSubTypeControl(
                        data[UploadedDocumentTypeSelector] as string,
                        subTypeControls[0] as SelectControl
                    );

                    formArray.controls.map((formGroup: FormGroup) => {
                        const documentId = formGroup.controls[UploadedDocumentIdSelector].value;
                        const documentSubTypeControl = formGroup.controls[UploadedDocumentSubTypeSelector];

                        if ((documentId === data[UploadedDocumentIdSelector])) {
                            documentSubTypeControl.setValidators(subTypeControls[0].validators);
                            documentSubTypeControl.setValue(null, {emitEvent: false});
                        }
                    });
                }
            }
        }
    }

    private getDocumentsRequests(
        document: IndividualDocumentModel | CompanyDocumentModel,
        formArrayValues: { [key: string]: number | string }[]
    ): Observable<ApiResponse<ApiError[] | boolean>> {
        const formDocumentsData = formArrayValues.filter((item: { [key: string]: number | string }) => {
            return item[UploadedDocumentIdSelector] === document.id;
        });

        if (formDocumentsData.length === 0) {
            return Helper.createSuccessResponseObservable<boolean>(true);
        }

        let updateRequest = null;

        if (
            (formDocumentsData[0][UploadedDocumentTypeSelector] !== document.documentType) ||
            (formDocumentsData[0][UploadedDocumentSubTypeSelector] !== document.subDocumentType)
        ) {
            const clonedModel = !this.isCorporateClient ?
                document.serialize() as IndividualDocumentInterface :
                document.serialize() as CompanyDocumentInterface;

            document.documentType = formDocumentsData[0][UploadedDocumentTypeSelector] as string;
            document.subDocumentType = formDocumentsData[0][UploadedDocumentSubTypeSelector] as string;

            const updateDocumentRequest = !this.isCorporateClient ?
                this.individualDocumentsService.update(document as IndividualDocumentModel) :
                this.companyDocumentsService.update(document as CompanyDocumentModel);


            updateRequest = updateDocumentRequest.pipe(
                switchMap(this.updateRequestMap.bind(this, clonedModel, document))
            );
        } else {
            updateRequest = Helper.createSuccessResponseObservable<boolean>(true);
        }

        if (document.status !== formDocumentsData[0][UploadedDocumentStatusSelector]) {
            return this.extendUpdateRequest(updateRequest, formDocumentsData[0], document);
        }

        return updateRequest;
    }

    private updateRequestMap(
        clonedModel: IndividualDocumentInterface | CompanyDocumentInterface,
        document: IndividualDocumentModel | CompanyDocumentModel,
        response: ApiResponse<ApiError[] | null>
    ): ApiResponse<ApiError[] | boolean> {
        if (response.status !== HttpStatusCodesEnum.Ok) {
            const model = !this.isCorporateClient ?
                new IndividualDocumentModel(clonedModel as IndividualDocumentInterface) :
                new CompanyDocumentModel(clonedModel as CompanyDocumentInterface);

            document.documentType = model.documentType;
            document.subDocumentType = model.subDocumentType;

            return Helper.errorsHelper().createErrorResponse<null>(response);
        }

        this.updateAvailableDocumentsStatusTriggers(document);

        return Helper.createSuccessResponse<boolean>(true);
    }

    private updateAvailableDocumentsStatusTriggers(document: IndividualDocumentModel | CompanyDocumentModel): void {
        const filteredTriggers = this.availableDocumentsStatusTriggers
            .filter((trigger: UploadedDocumentInitDataInterface) => trigger.id === document.id);

        if (filteredTriggers.length !== 0) {
            filteredTriggers[0].type = document.documentType;
            filteredTriggers[0].subType = document.subDocumentType;
        }
    }

    private extendUpdateRequest(
        updateRequest: Observable<ApiResponse<ApiError[] | boolean>>,
        formDocumentData: { [key: string]: number | string },
        document: IndividualDocumentModel | CompanyDocumentModel
    ): Observable<ApiResponse<ApiError[] | boolean>> {
        return updateRequest.pipe(
            switchMap((response: ApiResponse<ApiError[] | boolean>) => {
                if (response.status !== HttpStatusCodesEnum.Ok) {
                    return Helper.errorsHelper().createErrorResponseObservable<boolean>(response);
                }

                const status = formDocumentData[UploadedDocumentStatusSelector] as number;
                const trigger = Helper.enumsHelper().getDocumentStatusTriggerByStatus(status);

                const model = !this.isCorporateClient ?
                    new IndividualDocumentStatusTriggerModel(document.id, trigger) :
                    new CompanyDocumentStatusTriggerModel(document.id, trigger);

                const changeDocumentStatus = !this.isCorporateClient ?
                    this.individualDocumentsService.changeStatus(model as IndividualDocumentStatusTriggerModel) :
                    this.companyDocumentsService.changeStatus(model as CompanyDocumentStatusTriggerModel);

                return changeDocumentStatus.pipe(
                    map((updateStatusResponse: ApiResponse<ApiError[] | null>) => {
                        if (updateStatusResponse.status !== HttpStatusCodesEnum.Ok) {
                            return Helper.errorsHelper().createErrorResponse<boolean>(response);
                        }

                        document.status = status;

                        this.availableDocumentsStatusTriggers = this.availableDocumentsStatusTriggers
                            .filter((statusTrigger: UploadedDocumentInitDataInterface) => {
                                return statusTrigger.id !== formDocumentData[UploadedDocumentIdSelector];
                            });

                        return Helper.createSuccessResponse<boolean>(true);
                    })
                );
            })
        );
    }

    initForm(): Observable<boolean> {
        this.form = new FormGroup({});
        this.controls = [];

        if (this.documents.length > 0) {
            const getAvailableStatusRequests = this.createRequestsForAvailableStatuses();

            return forkJoin(getAvailableStatusRequests).pipe(
                map((responses: ApiResponse<ApiError[] | UploadedDocumentInitDataInterface>[]) => {
                    if (Helper.errorsHelper().checkForErrorResponses<UploadedDocumentInitDataInterface>(responses)) {
                        return false;
                    }

                    const formArray = new FormArray([]);

                    responses.map((response: ApiResponse<ApiError[] | UploadedDocumentInitDataInterface>) => {
                        const data = response.data as UploadedDocumentInitDataInterface;
                        const formGroup = this.initDocumentFormGroup(data);

                        formArray.push(formGroup);
                    });

                    this.form.addControl(this.uploadedDocumentsFormArraySelector, formArray);

                    return true;
                })
            );
        }

        return of(false);
    }

    validateForm(messageFunction: (controlLabel?: string) => { [key: string]: string; }): boolean {
        if (this.form.invalid) {
            const formArray = this.form.controls[this.uploadedDocumentsFormArraySelector] as FormArray;

            formArray.controls.map((formGroup: FormGroup, formGroupIndex: number) => {
                if (formGroup.invalid) {
                    Object.keys(formGroup.controls).map((formControlKey: string) => {
                        if (formGroup.controls[formControlKey].invalid) {
                            this.controls[formGroupIndex].map((control: BaseControl) => {
                                if (control.name === formControlKey) {
                                    control.errors = Object.keys(formGroup.controls[formControlKey].errors)
                                        .map((errorKey: string) => {
                                            return messageFunction(control.placeholder)[errorKey];
                                        });
                                }
                            });
                        }
                    });
                }
            });

            return false;
        }

        return true;
    }

    createRequestsStack(): Observable<ApiResponse<ApiError[] | boolean>>[] {
        const formArrayValues = (this.form.controls[this.uploadedDocumentsFormArraySelector] as FormArray).value;

        if (!this.isCorporateClient) {
            return (this.documents as IndividualDocumentModel[]).map((document: IndividualDocumentModel) => {
                return this.getDocumentsRequests(document, formArrayValues);
            });
        }

        return (this.documents as CompanyDocumentModel[]).map((document: CompanyDocumentModel) => {
            return this.getDocumentsRequests(document, formArrayValues);
        });
    }
}
